import { useState } from 'react';
import {
  Heart, Users, DollarSign, Calculator, CalendarClock, Shield,
  AlertTriangle, ArrowDown, ArrowUp, FileText, Plus, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

// ==================== Helpers ====================

const fmtAUD = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const fmtPct = (v: number) => `${v.toFixed(1)}%`;

// ==================== Mock Data ====================

interface PensionMember {
  id: string;
  name: string;
  age: number;
  fund: string;
  pensionType: 'ABP' | 'TRIS';
  commencementDate: string;
  openingBalance: number;
  currentBalance: number;
  reversionaryBeneficiary: string;
  paidYTD: number;
  // Transfer Balance
  tbcUsed: number;
  tbcEvents: { date: string; type: string; amount: number }[];
  // Tax components
  taxFreeComponent: number;
  taxableComponent: number;
  // Beneficiaries
  beneficiaries: { name: string; relationship: string; dependant: boolean; proportion: number }[];
}

const TBC_CAP = 1_900_000;

const PENSION_MEMBERS: PensionMember[] = [
  {
    id: 'pm1',
    name: 'James Smith',
    age: 68,
    fund: 'Smith Family Super Fund',
    pensionType: 'ABP',
    commencementDate: '01/07/2023',
    openingBalance: 920_000,
    currentBalance: 895_000,
    reversionaryBeneficiary: 'Sarah Smith (spouse)',
    paidYTD: 52_000,
    tbcUsed: 920_000,
    tbcEvents: [
      { date: '01/07/2023', type: 'Commencement', amount: 920_000 },
    ],
    taxFreeComponent: 280_000,
    taxableComponent: 615_000,
    beneficiaries: [
      { name: 'Sarah Smith', relationship: 'Spouse', dependant: true, proportion: 100 },
    ],
  },
  {
    id: 'pm2',
    name: 'Robert Johnson',
    age: 70,
    fund: 'Johnson Retirement Fund',
    pensionType: 'ABP',
    commencementDate: '15/01/2022',
    openingBalance: 1_450_000,
    currentBalance: 1_380_000,
    reversionaryBeneficiary: 'Margaret Johnson (spouse)',
    paidYTD: 78_000,
    tbcUsed: 1_650_000,
    tbcEvents: [
      { date: '15/01/2022', type: 'Commencement', amount: 1_200_000 },
      { date: '01/07/2024', type: 'Commencement (2nd pension)', amount: 500_000 },
      { date: '15/12/2024', type: 'Commutation', amount: -50_000 },
    ],
    taxFreeComponent: 410_000,
    taxableComponent: 970_000,
    beneficiaries: [
      { name: 'Margaret Johnson', relationship: 'Spouse', dependant: true, proportion: 60 },
      { name: 'David Johnson', relationship: 'Adult child', dependant: false, proportion: 40 },
    ],
  },
  {
    id: 'pm3',
    name: 'Margaret Johnson',
    age: 66,
    fund: 'Johnson Retirement Fund',
    pensionType: 'TRIS',
    commencementDate: '01/03/2025',
    openingBalance: 380_000,
    currentBalance: 365_000,
    reversionaryBeneficiary: 'Robert Johnson (spouse)',
    paidYTD: 18_000,
    tbcUsed: 380_000,
    tbcEvents: [
      { date: '01/03/2025', type: 'Commencement', amount: 380_000 },
    ],
    taxFreeComponent: 95_000,
    taxableComponent: 270_000,
    beneficiaries: [
      { name: 'Robert Johnson', relationship: 'Spouse', dependant: true, proportion: 100 },
    ],
  },
];

// Age-based minimum pension factors
const MIN_FACTORS: { label: string; minAge: number; maxAge: number; factor: number }[] = [
  { label: 'Under 65', minAge: 0, maxAge: 64, factor: 4 },
  { label: '65-74', minAge: 65, maxAge: 74, factor: 5 },
  { label: '75-79', minAge: 75, maxAge: 79, factor: 6 },
  { label: '80-84', minAge: 80, maxAge: 84, factor: 7 },
  { label: '85-89', minAge: 85, maxAge: 89, factor: 9 },
  { label: '90-94', minAge: 90, maxAge: 94, factor: 11 },
  { label: '95+', minAge: 95, maxAge: 999, factor: 14 },
];

function getMinFactor(age: number) {
  return MIN_FACTORS.find(f => age >= f.minAge && age <= f.maxAge)?.factor || 4;
}

// ==================== Component ====================

export default function SMSFPensionsPage() {
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [ecpiMethod, setEcpiMethod] = useState<'segregated' | 'unsegregated'>('unsegregated');
  const [ecpiTotalIncome, setEcpiTotalIncome] = useState('95000');
  const [ecpiPensionAssets, setEcpiPensionAssets] = useState('1200000');
  const [ecpiTotalAssets, setEcpiTotalAssets] = useState('1850000');
  const [ecpiCertStatus, setEcpiCertStatus] = useState<'obtained' | 'pending' | 'not-required'>('obtained');

  // Fetch funds list
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const fundId = selectedFundId || fundsData?.[0]?.id || '';

  // Fetch members for selected fund (pension details come from member accounts)
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['smsf-members', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/members`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Use API data with mock fallback
  const pensionMembers: PensionMember[] = membersData?.pensionMembers || membersData || PENSION_MEMBERS;

  const isLoading = fundsLoading || membersLoading;

  const [commMemberId, setCommMemberId] = useState(PENSION_MEMBERS[0].id);
  const [commAmount, setCommAmount] = useState('100000');
  const commMember = pensionMembers.find(m => m.id === commMemberId) || pensionMembers[0];

  const ecpiPensionAssetsNum = parseFloat(ecpiPensionAssets) || 0;
  const ecpiTotalAssetsNum = parseFloat(ecpiTotalAssets) || 0;
  const ecpiTotalIncomeNum = parseFloat(ecpiTotalIncome) || 0;
  const ecpiPct = ecpiMethod === 'segregated' ? 100 : ecpiTotalAssetsNum > 0 ? (ecpiPensionAssetsNum / ecpiTotalAssetsNum) * 100 : 0;
  const ecpiExemptAmount = ecpiMethod === 'segregated' ? ecpiTotalIncomeNum : ecpiTotalIncomeNum * (ecpiPct / 100);

  const commAmountNum = parseFloat(commAmount) || 0;
  const commTaxFreePct = commMember.currentBalance > 0 ? commMember.taxFreeComponent / (commMember.taxFreeComponent + commMember.taxableComponent) : 0;
  const commTaxFreeReduce = commAmountNum * commTaxFreePct;
  const commTaxableReduce = commAmountNum * (1 - commTaxFreePct);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Heart}
        title="Pensions & Benefits"
        description="Manage pension payments, transfer balance caps and member benefits"
      />

      {/* Fund Selector */}
      {fundsData && fundsData.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-muted-foreground">Fund:</label>
          <Select value={fundId} onValueChange={setSelectedFundId}>
            <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select a fund..." /></SelectTrigger>
            <SelectContent>
              {fundsData.map((f: any) => (
                <SelectItem key={f.id} value={f.id}>{f.fund_name || f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      )}

      <Tabs defaultValue="pension-setup" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="pension-setup">Pension Setup</TabsTrigger>
          <TabsTrigger value="minimum-payments">Minimum Payments</TabsTrigger>
          <TabsTrigger value="transfer-balance">Transfer Balance</TabsTrigger>
          <TabsTrigger value="ecpi">ECPI</TabsTrigger>
          <TabsTrigger value="death-benefits">Death Benefits</TabsTrigger>
          <TabsTrigger value="commutations">Commutations</TabsTrigger>
        </TabsList>

        {/* ===== Pension Setup ===== */}
        <TabsContent value="pension-setup">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => toast.info('Pension setup wizard coming soon')}>
                <Plus className="h-4 w-4 mr-1.5" />
                Start New Pension
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {pensionMembers.map(m => (
                <Card key={m.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{m.name}</CardTitle>
                      <Badge variant={m.pensionType === 'ABP' ? 'default' : 'secondary'}>{m.pensionType}</Badge>
                    </div>
                    <CardDescription>{m.fund}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-y-2">
                      <span className="text-muted-foreground">Commencement</span>
                      <span className="text-right">{m.commencementDate}</span>
                      <span className="text-muted-foreground">Opening Balance</span>
                      <span className="text-right font-medium">{fmtAUD(m.openingBalance)}</span>
                      <span className="text-muted-foreground">Current Balance</span>
                      <span className="text-right font-bold">{fmtAUD(m.currentBalance)}</span>
                      <span className="text-muted-foreground">Reversionary</span>
                      <span className="text-right text-xs">{m.reversionaryBeneficiary}</span>
                      <span className="text-muted-foreground">Age</span>
                      <span className="text-right">{m.age}</span>
                    </div>
                    {m.pensionType === 'TRIS' && (
                      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        TRIS - maximum 10% drawdown applies
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ===== Minimum Payments ===== */}
        <TabsContent value="minimum-payments">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Minimum Pension Payment Tracker - FY 2025-26</CardTitle>
              <CardDescription>Based on 1 July balance and age-based minimum drawdown factors</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-center">Age</th>
                      <th className="pb-2 pr-4 font-medium text-right">1 July Balance</th>
                      <th className="pb-2 pr-4 font-medium text-center">Min Factor</th>
                      <th className="pb-2 pr-4 font-medium text-right">Minimum Amount</th>
                      <th className="pb-2 pr-4 font-medium text-right">Paid YTD</th>
                      <th className="pb-2 pr-4 font-medium text-right">Remaining</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pensionMembers.map(m => {
                      const factor = getMinFactor(m.age);
                      const minAmount = Math.round(m.openingBalance * (factor / 100));
                      const remaining = minAmount - m.paidYTD;
                      const pctPaid = minAmount > 0 ? (m.paidYTD / minAmount) * 100 : 0;
                      let status: string;
                      let variant: 'default' | 'destructive' | 'secondary';
                      if (pctPaid >= 100) {
                        status = 'Met';
                        variant = 'default';
                      } else if (pctPaid >= 70) {
                        status = 'On Track';
                        variant = 'secondary';
                      } else {
                        status = 'Underpaid';
                        variant = 'destructive';
                      }
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{m.name}</td>
                          <td className="py-3 pr-4 text-center">{m.age}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.openingBalance)}</td>
                          <td className="py-3 pr-4 text-center">{factor}%</td>
                          <td className="py-3 pr-4 text-right font-medium">{fmtAUD(minAmount)}</td>
                          <td className="py-3 pr-4 text-right">{fmtAUD(m.paidYTD)}</td>
                          <td className="py-3 pr-4 text-right">{remaining > 0 ? fmtAUD(remaining) : fmtAUD(0)}</td>
                          <td className="py-3"><Badge variant={variant}>{status}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Card className="bg-muted/50">
                <CardHeader className="pb-1 pt-3">
                  <CardTitle className="text-sm">Age-Based Minimum Drawdown Factors</CardTitle>
                </CardHeader>
                <CardContent className="pb-3">
                  <div className="flex flex-wrap gap-3 text-xs">
                    {MIN_FACTORS.map(f => (
                      <div key={f.label} className="rounded border bg-background px-2 py-1">
                        <span className="text-muted-foreground">{f.label}:</span> <span className="font-medium">{f.factor}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Transfer Balance ===== */}
        <TabsContent value="transfer-balance">
          <div className="space-y-4">
            {pensionMembers.map(m => {
              const remaining = TBC_CAP - m.tbcUsed;
              const usedPct = (m.tbcUsed / TBC_CAP) * 100;
              const overCap = remaining < 0;
              return (
                <Card key={m.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{m.name}</CardTitle>
                        <CardDescription>{m.fund}</CardDescription>
                      </div>
                      {overCap && (
                        <Badge variant="destructive">TBC Exceeded</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Transfer Balance Cap</p>
                        <p className="font-bold text-lg">{fmtAUD(TBC_CAP)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Used</p>
                        <p className="font-bold text-lg">{fmtAUD(m.tbcUsed)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Remaining</p>
                        <p className={cn('font-bold text-lg', overCap ? 'text-red-600' : 'text-green-600')}>
                          {fmtAUD(Math.max(remaining, 0))}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>{fmtPct(Math.min(usedPct, 100))} used</span>
                        <span>{fmtAUD(TBC_CAP)} cap</span>
                      </div>
                      <div className="h-3 w-full rounded-full bg-secondary overflow-hidden">
                        <div
                          className={cn('h-full rounded-full transition-all', overCap ? 'bg-red-500' : usedPct > 90 ? 'bg-amber-500' : 'bg-green-500')}
                          style={{ width: `${Math.min(usedPct, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">TBC Events</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="pb-1.5 pr-4 font-medium text-xs">Date</th>
                              <th className="pb-1.5 pr-4 font-medium text-xs">Type</th>
                              <th className="pb-1.5 font-medium text-xs text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.tbcEvents.map((e, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="py-2 pr-4 text-xs">{e.date}</td>
                                <td className="py-2 pr-4 text-xs">{e.type}</td>
                                <td className={cn('py-2 text-right text-xs font-medium', e.amount < 0 ? 'text-red-600' : '')}>
                                  {e.amount < 0 ? `(${fmtAUD(Math.abs(e.amount))})` : fmtAUD(e.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ===== ECPI ===== */}
        <TabsContent value="ecpi">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-4 w-4" /> ECPI Calculator
                </CardTitle>
                <CardDescription>Exempt Current Pension Income calculation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Method</label>
                  <Select value={ecpiMethod} onValueChange={v => setEcpiMethod(v as 'segregated' | 'unsegregated')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="segregated">Segregated</SelectItem>
                      <SelectItem value="unsegregated">Unsegregated (Proportionate)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Total Fund Income (FY)</label>
                  <Input type="number" value={ecpiTotalIncome} onChange={e => setEcpiTotalIncome(e.target.value)} />
                </div>

                {ecpiMethod === 'unsegregated' && (
                  <>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Pension Assets (average)</label>
                      <Input type="number" value={ecpiPensionAssets} onChange={e => setEcpiPensionAssets(e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Total Fund Assets (average)</label>
                      <Input type="number" value={ecpiTotalAssets} onChange={e => setEcpiTotalAssets(e.target.value)} />
                    </div>
                  </>
                )}

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Actuarial Certificate</label>
                  <Select value={ecpiCertStatus} onValueChange={v => setEcpiCertStatus(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="obtained">Obtained</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="not-required">Not Required (Segregated)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">ECPI Result</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Method</p>
                    <p className="font-medium capitalize">{ecpiMethod}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Actuarial Certificate</p>
                    <Badge variant={ecpiCertStatus === 'obtained' ? 'default' : ecpiCertStatus === 'pending' ? 'secondary' : 'outline'} className="capitalize">
                      {ecpiCertStatus.replace('-', ' ')}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Exempt Percentage</p>
                    <p className="font-bold text-2xl text-green-600">{fmtPct(ecpiPct)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Exempt Income</p>
                    <p className="font-bold text-2xl">{fmtAUD(Math.round(ecpiExemptAmount))}</p>
                  </div>
                </div>

                <div className="rounded-lg border p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Fund Income</span>
                    <span>{fmtAUD(ecpiTotalIncomeNum)}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Less: Exempt Income</span>
                    <span>({fmtAUD(Math.round(ecpiExemptAmount))})</span>
                  </div>
                  <div className="border-t pt-1 flex justify-between font-bold">
                    <span>Assessable Income</span>
                    <span>{fmtAUD(Math.round(ecpiTotalIncomeNum - ecpiExemptAmount))}</span>
                  </div>
                </div>

                {ecpiMethod === 'unsegregated' && ecpiCertStatus !== 'obtained' && (
                  <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Actuarial certificate required for unsegregated method. Ensure it is obtained before lodgement.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== Death Benefits ===== */}
        <TabsContent value="death-benefits">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Death Benefit Analysis</CardTitle>
              <CardDescription>Tax impact based on dependency status and benefit components</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-right">Tax-Free</th>
                      <th className="pb-2 pr-4 font-medium text-right">Taxable</th>
                      <th className="pb-2 pr-4 font-medium text-right">Total Balance</th>
                      <th className="pb-2 font-medium">Beneficiaries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pensionMembers.map(m => (
                      <tr key={m.id} className="border-b last:border-0 align-top">
                        <td className="py-3 pr-4 font-medium">{m.name}</td>
                        <td className="py-3 pr-4 text-right">{fmtAUD(m.taxFreeComponent)}</td>
                        <td className="py-3 pr-4 text-right">{fmtAUD(m.taxableComponent)}</td>
                        <td className="py-3 pr-4 text-right font-bold">{fmtAUD(m.taxFreeComponent + m.taxableComponent)}</td>
                        <td className="py-3">
                          <div className="space-y-2">
                            {m.beneficiaries.map((b, i) => {
                              const bTaxable = m.taxableComponent * (b.proportion / 100);
                              const taxImpact = b.dependant ? 0 : Math.round(bTaxable * 0.15 + bTaxable * 0.02);
                              return (
                                <div key={i} className="rounded border p-2 text-xs space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{b.name} ({b.relationship})</span>
                                    <Badge variant={b.dependant ? 'default' : 'outline'}>
                                      {b.dependant ? 'Dependant' : 'Non-Dependant'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between text-muted-foreground">
                                    <span>Share: {b.proportion}%</span>
                                    <span>
                                      Tax impact: {b.dependant
                                        ? <span className="text-green-600 font-medium">Tax-free</span>
                                        : <span className="text-red-600 font-medium">{fmtAUD(taxImpact)} (est.)</span>
                                      }
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Card className="bg-muted/50">
                <CardHeader className="pb-1 pt-3">
                  <CardTitle className="text-sm">Death Benefit Payment Options</CardTitle>
                </CardHeader>
                <CardContent className="pb-3">
                  <div className="flex flex-col md:flex-row items-start gap-4 text-xs text-muted-foreground">
                    <div className="flex items-start gap-2 flex-1 rounded border bg-background p-3">
                      <ArrowDown className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground mb-1">Dependant Beneficiary</p>
                        <p>Lump sum (tax-free) or pension income stream. No tax on either the tax-free or taxable component.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 flex-1 rounded border bg-background p-3">
                      <ArrowUp className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground mb-1">Non-Dependant Beneficiary</p>
                        <p>Lump sum only. Tax-free component is tax-free. Taxable component taxed at 15% + 2% Medicare levy.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 flex-1 rounded border bg-background p-3">
                      <FileText className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground mb-1">Estate / Legal Rep</p>
                        <p>Paid to LPR for distribution. Tax depends on ultimate beneficiary status.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Commutations ===== */}
        <TabsContent value="commutations">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-4 w-4" /> Commutation Calculator
                </CardTitle>
                <CardDescription>Calculate the impact of commuting part of a pension</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Select Member</label>
                  <Select value={commMemberId} onValueChange={setCommMemberId}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {pensionMembers.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-muted-foreground">Current Balance</span>
                  <span className="text-right font-medium">{fmtAUD(commMember.currentBalance)}</span>
                  <span className="text-muted-foreground">Tax-Free Component</span>
                  <span className="text-right">{fmtAUD(commMember.taxFreeComponent)} ({fmtPct(commTaxFreePct * 100)})</span>
                  <span className="text-muted-foreground">Taxable Component</span>
                  <span className="text-right">{fmtAUD(commMember.taxableComponent)} ({fmtPct((1 - commTaxFreePct) * 100)})</span>
                  <span className="text-muted-foreground">TBC Used</span>
                  <span className="text-right">{fmtAUD(commMember.tbcUsed)}</span>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Commutation Amount</label>
                  <Input type="number" value={commAmount} onChange={e => setCommAmount(e.target.value)} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Commutation Impact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {commAmountNum > commMember.currentBalance && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Commutation amount exceeds current balance.
                  </div>
                )}

                <div className="rounded-lg border p-4 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Commutation Amount</span>
                    <span className="font-medium">{fmtAUD(commAmountNum)}</span>
                  </div>
                  <div className="pl-4 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax-Free Portion</span>
                      <span>{fmtAUD(Math.round(commTaxFreeReduce))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Taxable Portion</span>
                      <span>{fmtAUD(Math.round(commTaxableReduce))}</span>
                    </div>
                  </div>
                  <div className="border-t pt-2 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Remaining Balance</span>
                      <span className="font-bold">{fmtAUD(Math.max(commMember.currentBalance - commAmountNum, 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Remaining Tax-Free</span>
                      <span>{fmtAUD(Math.round(Math.max(commMember.taxFreeComponent - commTaxFreeReduce, 0)))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Remaining Taxable</span>
                      <span>{fmtAUD(Math.round(Math.max(commMember.taxableComponent - commTaxableReduce, 0)))}</span>
                    </div>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TBC Credit Released</span>
                      <span className="font-medium text-green-600">{fmtAUD(commAmountNum)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">New TBC Used</span>
                      <span className="font-medium">{fmtAUD(Math.max(commMember.tbcUsed - commAmountNum, 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TBC Remaining</span>
                      <span className="font-bold text-green-600">
                        {fmtAUD(TBC_CAP - Math.max(commMember.tbcUsed - commAmountNum, 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => toast.success(`Commutation of ${fmtAUD(commAmountNum)} for ${commMember.name} - preview only (not processed)`)}
                >
                  Preview Commutation
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
